import { useCallback } from "react"
import { GroupItem } from "common/types/graphql"
import { useViewer } from "common/hooks/data/useViewer"
import { useViewerSubscriptions } from "common/hooks/data/useViewerSubscriptions"
import { useSubscribedNewslettersContext } from "modules/newsletters/contexts"
import { useRegionCode } from "modules/i18n/hooks/useRegionCode"
import { useNotifications } from "common/components/Notification"
import { useTranslations } from "modules/i18n/hooks/useTranslations"
import {
  ErrorResponseFormat,
  useToggleSubscription,
} from "modules/newsletters/hooks/useToggleSubscription"
import { useNewslettersV2Context } from "../../contexts"
import { NewsletterCard } from "../NewsletterCard"
import {
  trackNewsletterSubCardDeselected,
  trackNewsletterSubCardSelected,
} from "../../../../common/lib/tcEventCollector"

const NOTIFICATION_TIMEOUT = 5000 // In milliseconds

export interface NewsletterSubscriberProps {
  newsletter: GroupItem
  rank: number
}

/**
 * Container component for newsletter subscription logic in v2 design.
 * Handles both signed-in and anonymous user subscription flows.
 *
 * => Anonymous users context: we will only toggle selected newsletters in the context until they submit their email.
 * => Anonymous users after submission: we prevent unsubscription but allow additional subscriptions with ReCAPTCHA.
 * => Signed-in users context: we will toggle selected newsletters in the context and make a request to the backend to
 * subscribe/unsubscribe the user to the selected newsletters.
 */
export const NewsletterSubscriber = ({ newsletter, rank }: NewsletterSubscriberProps) => {
  const regionCode = useRegionCode()
  const { ids: newsletterIds, toggle: toggleContext } = useSubscribedNewslettersContext()
  const { set: setNotifications } = useNotifications()
  const { isSignedIn } = useViewer()
  const { getPrimaryEmailAddress } = useViewerSubscriptions()
  const { email: anonymousEmail, isSubscribed, recaptchaRef } = useNewslettersV2Context()
  const { toggleSubscription, isLoading } = useToggleSubscription()
  const t = useTranslations("newsletters")

  const isSelected = newsletterIds.has(newsletter.id)

  const handleToggle = useCallback(() => {
    if (isLoading) {
      return
    }

    const { id } = newsletter
    const isCurrentlySubscribed = newsletterIds.has(id)
    const newsletterListIds = new Set([id])

    /**
     * First, let's handle anonymous users
     */

    if (!isSignedIn) {
      /**
       * Prevent anonymous users from unsubscribing after email submission.
       */
      if (isSubscribed && isCurrentlySubscribed) {
        window.alert(t("v2.anonymous_unsubscribe"))
        return
      }

      /**
       * They check / uncheck a newsletter...
       */
      toggleContext(id)

      /**
       * In case they already have subscribed to a newsletter, let's allow them to keep subscribing.
       */

      if (isSubscribed) {
        void (async () => {
          const recaptchaToken = (await recaptchaRef.current?.executeAsync()) ?? undefined

          try {
            await toggleSubscription({
              regionCode,
              email: anonymousEmail,
              action: "subscribe",
              newsletterListIds,
              recaptchaToken,
            })
          } catch (error) {
            const { errors } = error as ErrorResponseFormat
            const errorMessage = errors[0]
            setNotifications([{ type: "error", title: errorMessage }])
            toggleContext(id)
          }
        })()
      }

      /**
       * Track the selection / deselection event
       */
      if (isCurrentlySubscribed) {
        trackNewsletterSubCardDeselected({ newsletterId: id, rank, userLoggedIn: false })
      } else {
        trackNewsletterSubCardSelected({ newsletterId: id, rank, userLoggedIn: false })
      }

      return
    }

    /**
     * Now we can handle the signed-in scenarios.
     */
    const email = getPrimaryEmailAddress()
    const action = isCurrentlySubscribed ? "unsubscribe" : "subscribe"
    const message =
      action === "subscribe"
        ? t("v2.notification.subscribed_to", { newsletterName: newsletter.name })
        : t("v2.notification.unsubscribed_from", { newsletterName: newsletter.name })
    toggleContext(id)

    if (isCurrentlySubscribed) {
      trackNewsletterSubCardDeselected({ newsletterId: id, rank, userLoggedIn: true })
    } else {
      trackNewsletterSubCardSelected({ newsletterId: id, rank, userLoggedIn: true })
    }

    void (async () => {
      try {
        await toggleSubscription({
          regionCode,
          email,
          action,
          newsletterListIds,
        })
        setNotifications({
          type: "success",
          title: message,
          duration: NOTIFICATION_TIMEOUT,
        })
      } catch (error) {
        const { errors } = error as ErrorResponseFormat
        const errorMessage = errors[0]
        setNotifications([{ type: "error", title: errorMessage }])
        toggleContext(id)
      }
    })()
  }, [
    isLoading,
    newsletter,
    newsletterIds,
    isSignedIn,
    isSubscribed,
    toggleContext,
    getPrimaryEmailAddress,
    toggleSubscription,
    anonymousEmail,
    recaptchaRef,
    regionCode,
    setNotifications,
    rank,
    t,
  ])

  return (
    <NewsletterCard
      newsletter={newsletter}
      selected={isSelected}
      loading={isLoading}
      isSubscribed={isSubscribed}
      onToggle={handleToggle}
      regionCode={regionCode}
    />
  )
}
